import { Component, computed, DestroyRef, inject, input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppState } from '@app/reducers/interfaces';
import { DialogConfirmHighImpactComponent } from '@app/shared/sb-lib/dialog-confirm-high-impact/dialog-confirm-high-impact.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconDelete2CuteRe } from '@sb/mgc-icons/mgcIcon-delete_2_cute_re.icon';
import { mgcIconLockCuteRe } from '@sb/mgc-icons/mgcIcon-lock_cute_re.icon';
import { mgcIconMailSendCuteRe } from '@sb/mgc-icons/mgcIcon-mail_send_cute_re.icon';
import { mgcIconMore2Regular } from '@sb/mgc-icons/mgcIcon-more_2_regular.icon';
import { mgcIconUnlockCuteRe } from '@sb/mgc-icons/mgcIcon-unlock_cute_re.icon';
import { usePermission } from '@sb/shared/permission/data-access';
import { IconButtonComponent } from '@sb/ui/button';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import {
  MenuComponent,
  MenuDeferredContentDirective,
  MenuItemComponent,
  MenuSeparatorComponent,
  MenuTriggerDirective,
} from '@sb/ui/menu';
import { QuotaType } from '@shiftbase-com/ts-shared/models';
import { filter, switchMap, take } from 'rxjs';

import { getAccountSubscription } from '../../../../../reducers/account/account-service.selectors';
import { EmployeeWithActiveContractAndDepartments } from '../../../../../reducers/orm/employee/employee.model';
import { EmployeeService } from '../../../../../reducers/orm/employee/employee.service';
import { hasReachedQuota } from '../../../../../shared/subscription-plan/subscription-quota.directive';

@Component({
  selector: 'employee-list-action',
  templateUrl: './employee-list-action.component.html',
  imports: [
    TranslatePipe,
    MenuComponent,
    MenuDeferredContentDirective,
    MenuItemComponent,
    MenuSeparatorComponent,
    MenuTriggerDirective,
    IconButtonComponent,
    IconComponent,
    DialogConfirmHighImpactComponent,
  ],
})
export class EmployeeListActionComponent {
  #store = inject<Store<AppState>>(Store);
  private employeeService = inject(EmployeeService);
  private destroyRef = inject(DestroyRef);

  employee = input.required<EmployeeWithActiveContractAndDepartments>();

  @ViewChild(DialogConfirmHighImpactComponent, { static: false })
  private dialogConfirmHighImpactComponent!: DialogConfirmHighImpactComponent;

  quotaType = QuotaType;

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconMore2Regular,
      mgcIconMailSendCuteRe,
      mgcIconUnlockCuteRe,
      mgcIconLockCuteRe,
      mgcIconDelete2CuteRe,
    ]);
  }

  #subscription = this.#store.selectSignal(getAccountSubscription);

  #mayActivate = usePermission(() => ({
    userId: this.employee().id,
    permissions: 'Activate users',
    departments: this.employee().departments,
  }));
  #mayAnonymize = usePermission(() => ({
    userId: this.employee().id,
    permissions: 'Delete users',
    departments: this.employee().departments,
  }));
  #mayDeactivate = usePermission(() => ({ userId: this.employee().id, permissions: 'Activate users' }));
  #mayInvite = usePermission(() => ({ userId: this.employee().id, permissions: 'Edit users' }));

  #hasEmail = computed(() => this.employee().email?.length > 0);

  canActivate = computed(() => {
    const subscription = this.#subscription();
    const seatAvailable = !subscription || !hasReachedQuota(QuotaType.SEATS, subscription);

    return this.#mayActivate() && seatAvailable && this.employee().isActive === false;
  });
  canAnonymize = computed(() => this.#mayAnonymize() && !this.employee().isActive && !this.employee().anonymized);
  canDeactivate = computed(() => this.#mayDeactivate() && !!this.employee().isActive);
  canInvite = computed(() => this.#mayInvite() && this.#hasEmail() && this.employee().invited === false);
  canResendInvite = computed(() => this.#mayInvite() && this.#hasEmail() && this.employee().invited === true);

  canViewMenu = computed(
    () =>
      this.canActivate() || this.canAnonymize() || this.canDeactivate() || this.canInvite() || this.canResendInvite(),
  );

  activate(employeeId: string) {
    void this.employeeService.activate(employeeId).subscribe();
  }

  deactivate(employeeId: string) {
    void this.employeeService.remove(employeeId).subscribe();
  }

  invite(employeeId: string) {
    const saveData = {
      id: employeeId,
      User: {
        id: employeeId,
        notify_employee: true,
      },
    };
    void this.employeeService.save(saveData).subscribe();
  }

  resendEmployeeInvite(employeeId: string) {
    void this.employeeService.resendInvite({ ids: [employeeId] }).subscribe();
  }

  anonymize() {
    const employeeId = this.employee().id;

    if (!employeeId) {
      return;
    }

    void this.dialogConfirmHighImpactComponent
      .openDialog()
      .pipe(
        take(1),
        filter((result) => result?.confirmed),
        switchMap(() => this.employeeService.anonymize(employeeId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
